/**
 * CollabCalendar – månedsview for skaperens egen tilgjengelighet og
 * kommende samarbeid-avtaler. Klikk dato → toggle ledig/opptatt eller
 * åpne avtale-detalj hvis det er booket.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight, Plus, X, Send, CalendarPlus, Pencil } from "lucide-react";
import { toast } from "sonner";
import { useSearchParams, Link } from "react-router-dom";
import api from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import SlotEditor from "./SlotEditor";
import ManualAgreementModal from "./ManualAgreementModal";
import CollabCalendarList from "./CollabCalendarList";
import { authView } from "./AuthMedia";

function monthRange(year, month) {
  // First day of month
  const first = new Date(Date.UTC(year, month, 1));
  const last = new Date(Date.UTC(year, month + 1, 0));
  const pad = (n) => String(n).padStart(2, "0");
  const toISO = (d) => `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
  return { start: toISO(first), end: toISO(last), first, last };
}

const STATUS_COLOR = {
  proposed: "#F4B400",  // gul – RESERVERT (ventende utgående forespørsel)
  accepted: "var(--gold)",
  posted: "#7BBF6E",
  confirmed: "#46B85B",
};

const STATUS_LABEL = {
  proposed: "Reservert",
  accepted: "Akseptert",
  posted: "Postet",
  confirmed: "Bekreftet",
  declined: "Avslått",
  cancelled: "Avlyst",
  disputed: "Tvist",
};

const TYPE_COLOR = {
  sfs: "#E63946",         // rød
  mass: "#4DA8DA",        // blå
  l4l: "#F4B400",         // gul
  story_swap: "#46B85B",  // grønn
};

export default function CollabCalendar() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const now = new Date();
  const [year, setYear] = useState(now.getUTCFullYear());
  const [month, setMonth] = useState(now.getUTCMonth());
  const [slots, setSlots] = useState([]);
  const [requests, setRequests] = useState([]);
  const [externalMessages, setExternalMessages] = useState([]);
  const [manualAgreements, setManualAgreements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingDate, setEditingDate] = useState(null);
  const [manualModal, setManualModal] = useState(null); // {initialDate?, existing?, initialType?, initialPlatform?}
  const [dayOverviewType, setDayOverviewType] = useState(null);
  const [viewMode, setViewMode] = useState("grid"); // "grid" | "list"
  const [accounts, setAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [, setSearchParams] = useSearchParams();

  const { start, end, first } = useMemo(() => monthRange(year, month), [year, month]);

  const load = () => {
    setLoading(true);
    api.get(`/collab/calendar/me?start=${start}&end=${end}`)
      .then((r) => {
        setSlots(r.data.slots || []);
        setRequests(r.data.requests || []);
        setExternalMessages(r.data.external_messages || []);
        setManualAgreements(r.data.manual_agreements || []);
      })
      .catch(() => { setSlots([]); setRequests([]); setExternalMessages([]); setManualAgreements([]); })
      .finally(() => setLoading(false));
  };
  useEffect(load, [start, end]); // eslint-disable-line

  useEffect(() => {
    api.get("/me/collab-accounts")
      .then((r) => {
        const list = r.data?.results || [];
        setAccounts(list);
        if (list.length > 0) {
          setSelectedAccountId((prev) => prev || list[0].id);
        }
      })
      .catch(() => setAccounts([]));
  }, []); // eslint-disable-line

  // Bygg ukenett
  const days = [];
  // Mandag-først: 0=Sun → vil bli sist
  const startWeekday = (first.getUTCDay() + 6) % 7;
  for (let i = 0; i < startWeekday; i++) days.push(null);
  const last = new Date(Date.UTC(year, month + 1, 0));
  for (let d = 1; d <= last.getUTCDate(); d++) {
    const pad = (n) => String(n).padStart(2, "0");
    days.push(`${year}-${pad(month + 1)}-${pad(d)}`);
  }

  const slotByDate = Object.fromEntries(slots.map((s) => [s.date, s]));
  const requestsByDate = requests.reduce((acc, r) => {
    const d = r.scheduled_date;
    if (!acc[d]) acc[d] = [];
    acc[d].push(r);
    return acc;
  }, {});
  const extByDate = externalMessages.reduce((acc, m) => {
    const d = m.slot_date;
    if (!d) return acc;
    if (!acc[d]) acc[d] = [];
    acc[d].push(m);
    return acc;
  }, {});
  const manualByDate = manualAgreements.reduce((acc, m) => {
    if (!acc[m.date]) acc[m.date] = [];
    acc[m.date].push(m);
    return acc;
  }, {});

  const monthLabel = new Date(Date.UTC(year, month, 1)).toLocaleDateString("nb-NO", { month: "long", year: "numeric" });
  const prevMonth = () => {
    if (month === 0) { setMonth(11); setYear(year - 1); } else setMonth(month - 1);
  };
  const nextMonth = () => {
    if (month === 11) { setMonth(0); setYear(year + 1); } else setMonth(month + 1);
  };

  const [showDayOverview, setShowDayOverview] = useState(null); // date string

  const onDateClick = (date) => {
    if (!date) return;
    // Kalender er kun oversikt — åpne alltid dag-oversikt (ikke slot-editor)
    setShowDayOverview(date);
  };

  const onSlotSaved = () => { load(); };

  const onSlotDelete = async (date) => {
    if (!window.confirm(t("collab.confirm_delete_slot"))) return;
    try {
      await api.delete(`/collab/calendar/me/${date}`);
      toast.success(t("collab.slot_deleted"));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("collab.save_err"));
    }
  };

  return (
    <section data-testid="collab-calendar">
      {/* Toggle Grid / Liste */}
      <div className="flex items-center gap-2 mb-4" data-testid="cal-view-toggle">
        <button
          onClick={() => setViewMode("grid")}
          className={`px-3 py-1.5 border font-meta text-xs uppercase tracking-wider ${
            viewMode === "grid"
              ? "border-[var(--gold)] text-[var(--gold)] bg-[var(--gold)]/10"
              : "border-[var(--cream-faint)]/30 text-[var(--cream-faint)] hover:border-[var(--gold)]/60"
          }`}
          data-testid="cal-view-grid"
        >
          {t("collab_list.view_grid", "Grid")}
        </button>
        <button
          onClick={() => setViewMode("list")}
          className={`px-3 py-1.5 border font-meta text-xs uppercase tracking-wider ${
            viewMode === "list"
              ? "border-[var(--gold)] text-[var(--gold)] bg-[var(--gold)]/10"
              : "border-[var(--cream-faint)]/30 text-[var(--cream-faint)] hover:border-[var(--gold)]/60"
          }`}
          data-testid="cal-view-list"
        >
          {t("collab_list.view_list", "Liste")}
        </button>
      </div>

      {viewMode === "grid" && (<>
      <div className="flex items-center justify-between mb-4">
        <button onClick={prevMonth} className="p-2 text-[var(--cream-soft)] hover:text-[var(--magenta)]" data-testid="cal-prev">
          <ChevronLeft className="h-5 w-5" />
        </button>
        <h2 className="font-fraunces italic text-2xl text-[var(--cream)] capitalize" data-testid="cal-month-label">{monthLabel}</h2>
        <button onClick={nextMonth} className="p-2 text-[var(--cream-soft)] hover:text-[var(--magenta)]" data-testid="cal-next">
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-end gap-2 mb-4">
        <button onClick={() => setManualModal({})} type="button"
          className="px-3 py-1.5 border border-[var(--magenta)]/60 text-[var(--magenta)] hover:bg-[var(--magenta)]/10 font-meta text-xs inline-flex items-center gap-1.5"
          data-testid="add-manual-agreement">
          <CalendarPlus className="h-3.5 w-3.5" /> + Legg til manuell avtale
        </button>
      </div>

      {/* Weekday header */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {["MAN", "TIR", "ONS", "TOR", "FRE", "LØR", "SØN"].map((d) => (
          <div key={d} className="text-center font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] py-2">{d}</div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1" data-testid="calendar-grid">
        {days.map((d, i) => {
          if (!d) return <div key={i} />;
          const slot = slotByDate[d];
          const reqs = requestsByDate[d] || [];
          const manuals = manualByDate[d] || [];
          const isPast = d < new Date().toISOString().slice(0, 10);
          const hasReqs = reqs.length > 0;
          const hasManual = manuals.length > 0;
          const topReqStatus = hasReqs ? reqs[0].status : null;
          return (
            <button
              key={d}
              onClick={() => onDateClick(d)}
              data-testid={`cal-day-${d}`}
              className={`relative min-h-[72px] p-2 text-left border transition-colors ${
                isPast ? "border-white/5 text-[var(--cream-faint)] opacity-60" :
                hasManual ? "border-[var(--magenta)]/60 bg-[var(--magenta)]/15 hover:bg-[var(--magenta)]/25" :
                hasReqs ? "border-[var(--magenta)]/40 bg-[var(--magenta)]/10 hover:bg-[var(--magenta)]/20" :
                slot?.is_available ? "border-[var(--gold)]/40 bg-[var(--gold)]/10 hover:bg-[var(--gold)]/20" :
                slot && !slot.is_available ? "border-white/10 bg-black/40 text-[var(--cream-faint)]" :
                "border-white/10 hover:border-[var(--magenta)]/40"
              }`}
            >
              <div className="font-meta text-xs text-[var(--cream)]">{parseInt(d.slice(-2), 10)}</div>
              {slot && slot.is_available && !hasReqs && !hasManual && (
                <div className="font-meta text-[9px] text-[var(--gold)] mt-1">
                  {t("collab.available")}
                </div>
              )}
              {/* Type-prikker i nedre kant */}
              {slot && slot.types && slot.types.length > 0 && (
                <div className="absolute bottom-1.5 left-1.5 flex gap-1">
                  {slot.types.slice(0, 4).map((tp) => (
                    <span key={tp} className="block w-1.5 h-1.5 rounded-full"
                      style={{ backgroundColor: TYPE_COLOR[tp] || "var(--cream-faint)" }}
                      title={tp.toUpperCase()}
                    />
                  ))}
                </div>
              )}
              {slot && !slot.is_available && !hasReqs && !hasManual && (
                <div className="font-meta text-[9px] text-[var(--cream-faint)] mt-1">{t("collab.busy")}</div>
              )}
              {hasManual && (
                <div className="mt-1">
                  {manuals.slice(0, 2).map((m) => {
                    const typeLbl = m.type === "other" ? (m.type_other_label || "ANNET") : m.type.toUpperCase();
                    const partner = m.partner_handle ? ` · @${m.partner_handle}` : "";
                    return (
                      <div key={m.id} className="font-meta text-[9px] truncate text-[var(--magenta)]">
                        {typeLbl}{partner}
                      </div>
                    );
                  })}
                  {manuals.length > 2 && (
                    <div className="font-meta text-[9px] text-[var(--cream-faint)]">+{manuals.length - 2}</div>
                  )}
                </div>
              )}
              {hasReqs && !hasManual && (
                <div className="space-y-0.5 mt-1">
                  {reqs.slice(0, 2).map((r) => (
                    <div key={r.id} className="font-meta text-[9px] truncate"
                      style={{ color: STATUS_COLOR[r.status] || "var(--cream-soft)" }}>
                      {r.type.toUpperCase()} · {r.requester_id === user?.id ? r.target_username : r.requester_username}
                    </div>
                  ))}
                  {reqs.length > 2 && (
                    <div className="font-meta text-[9px] text-[var(--cream-faint)]">+{reqs.length - 2}</div>
                  )}
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Legend */}
      <div className="mt-6 panel-tight p-3">
        <div className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] mb-2">{t("collab.legend_types")}</div>
        <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-[var(--cream-soft)]">
          <TypeLegend color={TYPE_COLOR.sfs} label="SFS" />
          <TypeLegend color={TYPE_COLOR.mass} label="MASS" />
          <TypeLegend color={TYPE_COLOR.l4l} label="L4L" />
          <TypeLegend color={TYPE_COLOR.story_swap} label="STORY SWAP" />
        </div>
      </div>
      </>)}

      {viewMode === "list" && (
        <CollabCalendarList
          year={year}
          month={month}
          monthLabel={monthLabel}
          prevMonth={prevMonth}
          nextMonth={nextMonth}
          slots={slots}
          requests={requests}
          externalMessages={externalMessages}
          manualAgreements={manualAgreements}
          accounts={accounts}
          selectedAccountId={selectedAccountId}
          onSelectAccount={setSelectedAccountId}
          onDateClick={(d, tp) => { setDayOverviewType(tp || null); setShowDayOverview(d); }}
          currentUserId={user?.id}
        />
      )}

      {/* Mine forespørsler-list (innkommende + utgående) */}
      <RequestsInbox onOpen={(id) => setSearchParams({ request: id })} reload={load} />

      {editingDate && (
        <SlotEditor
          date={editingDate}
          existing={slotByDate[editingDate]}
          onClose={() => setEditingDate(null)}
          onSaved={onSlotSaved}
          onDelete={() => onSlotDelete(editingDate)}
        />
      )}

      {showDayOverview && (
        <DayOverview
          date={showDayOverview}
          slot={slotByDate[showDayOverview]}
          requests={requestsByDate[showDayOverview] || []}
          externalMessages={extByDate[showDayOverview] || []}
          manualAgreements={manualByDate[showDayOverview] || []}
          initialType={dayOverviewType}
          onClose={() => { setShowDayOverview(null); setDayOverviewType(null); }}
          onOpenRequest={(id) => { setSearchParams({ request: id }); setShowDayOverview(null); }}
          onEditManual={(m) => { setShowDayOverview(null); setManualModal({ existing: m }); }}
          onAddManual={(type) => {
            const acct = accounts.find((a) => a.id === selectedAccountId);
            setManualModal({
              initialDate: showDayOverview,
              initialType: type || dayOverviewType,
              initialPlatform: acct?.platform || null,
            });
            setShowDayOverview(null);
            setDayOverviewType(null);
          }}
          reload={load}
        />
      )}

      {manualModal && (
        <ManualAgreementModal
          initialDate={manualModal.initialDate}
          initialType={manualModal.initialType}
          initialPlatform={manualModal.initialPlatform}
          existing={manualModal.existing}
          onClose={() => setManualModal(null)}
          onSaved={() => { setManualModal(null); load(); }}
          onDeleted={() => { setManualModal(null); load(); }}
        />
      )}
    </section>
  );
}

function DayOverview({ date, slot, requests, externalMessages = [], manualAgreements = [], initialType, onClose, onOpenRequest, onEditManual, onAddManual, reload }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [busy, setBusy] = useState(null); // request id som har pågående aksjon
  const [manualBusy, setManualBusy] = useState(null);
  const [savedFlash, setSavedFlash] = useState({}); // key: `${id}_${side}` → HH:MM:SS
  const fileInputs = React.useRef({});

  const flashSaved = (key) => {
    const ts = new Date().toLocaleTimeString("nb-NO");
    setSavedFlash((prev) => ({ ...prev, [key]: ts }));
    setTimeout(() => {
      setSavedFlash((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }, 3500);
  };

  const toggleManualPosted = async (m, side) => {
    setManualBusy(m.id);
    try {
      await api.post(`/collab/manual-agreements/${m.id}/toggle-posted`, { side });
      flashSaved(`${m.id}_${side}`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("collab.save_err"));
    } finally {
      setManualBusy(null);
    }
  };

  const toggleExtPosted = async (m, side) => {
    setManualBusy(m.id);
    try {
      await api.post(`/collab/external-messages/${m.id}/toggle-posted`, { side });
      flashSaved(`${m.id}_${side}`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("collab.save_err"));
    } finally {
      setManualBusy(null);
    }
  };

  const releaseExtDate = async (m) => {
    if (!window.confirm("Frigjøre denne datoen? Dagen blir tilgjengelig igjen.")) return;
    setManualBusy(m.id);
    try {
      await api.post(`/collab/external-messages/${m.id}/release-date`);
      toast.success("Dato frigjort — nå tilgjengelig igjen.");
      reload();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke frigjøre.");
    } finally {
      setManualBusy(null);
    }
  };

  const toggleExtHandled = async (m) => {
    setManualBusy(m.id);
    try {
      const endpoint = m.handled ? "mark-unhandled" : "mark-handled";
      await api.post(`/collab/external-messages/${m.id}/${endpoint}`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("collab.save_err"));
    } finally {
      setManualBusy(null);
    }
  };

  const markPosted = async (req) => {
    setBusy(req.id);
    try {
      await api.post(`/collab/requests/${req.id}/mark-posted`, {});
      toast.success(t("collab.marked_posted", "Markert som postet"));
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("collab.save_err"));
    } finally {
      setBusy(null);
    }
  };

  const confirmRequest = async (req) => {
    setBusy(req.id);
    try {
      await api.post(`/collab/requests/${req.id}/confirm`);
      toast.success(t("collab.confirmed", "Bekreftet"));
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("collab.save_err"));
    } finally {
      setBusy(null);
    }
  };

  const uploadProof = async (req, file) => {
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) {
      toast.error(t("collab.err_file_too_big", "Filen er for stor (maks 20 MB)"));
      return;
    }
    setBusy(req.id);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/collab/requests/${req.id}/upload-proof`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(t("collab.proof_uploaded", "Bevis lastet opp"));
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("collab.save_err"));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center bg-black/80 backdrop-blur-sm p-4 pt-24 pb-12 overflow-y-auto"
      onClick={onClose} data-testid="day-overview">
      <div className="max-w-lg w-full panel-tight p-6 relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute right-3 top-3 text-[var(--cream-faint)] hover:text-[var(--magenta)]">
          <X className="h-4 w-4" />
        </button>
        <div className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] mb-1">{t("collab.day_overview")}</div>
        <h3 className="font-fraunces italic text-2xl text-[var(--cream)] mb-4">{date}</h3>

        {/* Manuelle avtaler (utenfor-siden) */}
        {manualAgreements.length > 0 && (
          <div className="mb-3 space-y-2" data-testid="day-manual-list">
            <div className="font-meta text-[10px] tracking-[0.2em] text-[var(--magenta)]">MANUELL AVTALE</div>
            {manualAgreements.map((m) => {
              const typeLbl = m.type === "other" ? (m.type_other_label || "ANNET") : m.type.toUpperCase();
              const plat = (m.partner_platform || "").toLowerCase();
              const platLabel = ({
                snapchat: "Snapchat", telegram: "Telegram", onlyfans: "OnlyFans",
                fansly: "Fansly", instagram: "Instagram", x: "X / Twitter",
                tiktok: "TikTok", reddit: "Reddit", bluesky: "Bluesky", other: "Annet",
              })[plat] || (m.partner_platform || "").toUpperCase();
              const linkFor = {
                telegram: (h) => `https://t.me/${h}`,
                snapchat: (h) => `https://www.snapchat.com/add/${h}`,
                instagram: (h) => `https://instagram.com/${h}`,
                x: (h) => `https://x.com/${h}`,
                tiktok: (h) => `https://tiktok.com/@${h}`,
                reddit: (h) => `https://reddit.com/user/${h}`,
                bluesky: (h) => `https://bsky.app/profile/${h}`,
                onlyfans: (h) => `https://onlyfans.com/${h}`,
                fansly: (h) => `https://fansly.com/${h}`,
              };
              const contactUrl = m.partner_handle && linkFor[plat]
                ? linkFor[plat](m.partner_handle) : null;
              const copyHandle = () => {
                if (!m.partner_handle) return;
                navigator.clipboard.writeText(m.partner_handle);
                toast.success(`Kopiert: @${m.partner_handle}`);
              };
              return (
                <div key={m.id} className="panel-tight p-3 border-[var(--magenta)]/40" data-testid={`day-manual-${m.id}`}>
                  <div className="flex items-baseline gap-2 flex-wrap mb-2">
                    <span className="font-meta text-[10px] px-2 py-0.5 border border-[var(--magenta)] text-[var(--magenta)] uppercase tracking-wider">
                      {typeLbl}
                    </span>
                    {m.partner_handle && (
                      <span className="font-fraunces italic text-[var(--cream)]">@{m.partner_handle}</span>
                    )}
                    {platLabel && (
                      <span className="font-meta text-[9px] tracking-[0.15em] text-[var(--cream-faint)]">{platLabel.toUpperCase()}</span>
                    )}
                  </div>
                  {m.note && (
                    <p className="text-xs text-[var(--cream-soft)] whitespace-pre-line mb-2">{m.note}</p>
                  )}
                  <div className="flex flex-wrap gap-2 mt-2">
                    {m.partner_handle && (
                      <button onClick={copyHandle} type="button"
                        className="btn-ghost text-[10px] inline-flex items-center gap-1"
                        data-testid={`manual-copy-${m.id}`}>
                        Kopier handle
                      </button>
                    )}
                    {contactUrl && (
                      <a href={contactUrl} target="_blank" rel="noopener noreferrer"
                        className="btn-ghost text-[10px] inline-flex items-center gap-1"
                        data-testid={`manual-open-${m.id}`}>
                        Åpne i {platLabel}
                      </a>
                    )}
                    <button onClick={() => onEditManual?.(m)} type="button"
                      className="btn-ghost text-[10px] inline-flex items-center gap-1"
                      data-testid={`manual-edit-${m.id}`}>
                      <Pencil className="h-3 w-3" /> Rediger / slett
                    </button>
                  </div>

                  {/* Postet-status: begge parter */}
                  <div className="mt-3 pt-3 border-t border-white/10 space-y-2">
                    <label className="flex items-center gap-2 cursor-pointer text-xs text-[var(--cream)]">
                      <input
                        type="checkbox"
                        checked={!!m.posted_by_creator}
                        onChange={() => toggleManualPosted(m, "creator")}
                        disabled={manualBusy === m.id}
                        className="w-4 h-4 accent-[var(--magenta)] cursor-pointer"
                        data-testid={`manual-posted-me-${m.id}`}
                      />
                      <span className="flex-1">
                        {t("collab.manual_posted_by_me", "Postet fra min side")}
                        {m.posted_by_creator_at && (
                          <span className="ml-2 text-[10px] font-meta text-[var(--cream-faint)]">
                            ({new Date(m.posted_by_creator_at).toLocaleString("nb-NO")})
                          </span>
                        )}
                        {savedFlash[`${m.id}_creator`] && (
                          <span className="ml-2 text-[10px] font-meta text-[#46B85B]">
                            ✓ {t("collab.saved_at", "Lagret")} {savedFlash[`${m.id}_creator`]}
                          </span>
                        )}
                      </span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer text-xs text-[var(--cream)]">
                      <input
                        type="checkbox"
                        checked={!!m.posted_by_partner}
                        onChange={() => toggleManualPosted(m, "partner")}
                        disabled={manualBusy === m.id}
                        className="w-4 h-4 accent-[var(--magenta)] cursor-pointer"
                        data-testid={`manual-posted-partner-${m.id}`}
                      />
                      <span className="flex-1">
                        {t("collab.manual_posted_by_partner", "Postet fra motparten")}
                        {m.partner_handle && (
                          <span className="text-[var(--cream-soft)]"> (@{m.partner_handle})</span>
                        )}
                        {m.posted_by_partner_at && (
                          <span className="ml-2 text-[10px] font-meta text-[var(--cream-faint)]">
                            ({new Date(m.posted_by_partner_at).toLocaleString("nb-NO")})
                          </span>
                        )}
                        {savedFlash[`${m.id}_partner`] && (
                          <span className="ml-2 text-[10px] font-meta text-[#46B85B]">
                            ✓ {t("collab.saved_at", "Lagret")} {savedFlash[`${m.id}_partner`]}
                          </span>
                        )}
                      </span>
                    </label>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Legg til manuell avtale for denne dagen — alltid tilgjengelig */}
        <div className="mb-3">
          <button onClick={() => onAddManual?.(initialType)} type="button"
            className="btn-ghost text-xs inline-flex items-center gap-1.5"
            data-testid="day-add-manual">
            <Plus className="h-3 w-3" /> Legg til manuell avtale{initialType ? ` (${initialType.toUpperCase()})` : ""} for denne dagen
          </button>
        </div>

        {slot && slot.is_available && (
          <div className="mb-3 text-xs font-meta tracking-[0.15em] text-[var(--gold)] uppercase">
            {(slot.types || []).map((tp) => tp.toUpperCase()).join(" / ") || t("collab.legend_available").toUpperCase()}
          </div>
        )}
        {slot && !slot.is_available && (
          <div className="mb-3 panel-tight p-3 border-[var(--magenta)]/40 bg-[var(--magenta)]/5" data-testid="day-slot-busy">
            <div className="flex items-start justify-between gap-2 flex-wrap">
              <div>
                <div className="font-meta text-[10px] tracking-[0.2em] text-[var(--magenta)] mb-1">OPPTATT</div>
                {(slot.types || []).length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {slot.types.map((tp) => (
                      <span key={tp} className="font-meta text-[10px] px-2 py-0.5 border border-white/15 text-[var(--cream-soft)] uppercase tracking-wider">
                        {tp}
                      </span>
                    ))}
                  </div>
                )}
                {slot.note && (
                  <p className="text-xs text-[var(--cream-soft)] whitespace-pre-line">{slot.note}</p>
                )}
              </div>
              <button
                type="button"
                onClick={async () => {
                  if (!window.confirm("Frigjøre denne datoen? Dagen blir tilgjengelig igjen.")) return;
                  try {
                    await api.delete(`/collab/calendar/me/${date}`);
                    toast.success("Dato frigjort — nå tilgjengelig igjen.");
                    reload?.();
                    onClose?.();
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Kunne ikke frigjøre.");
                  }
                }}
                className="btn-ghost text-[10px] inline-flex items-center gap-1 shrink-0 border-[var(--gold)]/40 text-[var(--gold)] hover:bg-[var(--gold)]/10"
                data-testid="day-release-slot">
                Frigjør dato
              </button>
            </div>
          </div>
        )}

        {/* Eksterne henvendelser knyttet til denne dagen */}
        {externalMessages.length > 0 && (
          <div className="mb-3 space-y-2" data-testid="day-external-list">
            <div className="font-meta text-[10px] tracking-[0.2em] text-[var(--gold)]">EKSTERN HENVENDELSE</div>
            {externalMessages.map((m) => {
              const plat = (m.external_platform || "").toUpperCase();
              return (
                <div key={m.id} className="panel-tight p-3 border-[var(--gold)]/40" data-testid={`day-ext-${m.id}`}>
                  <div className="flex items-baseline gap-2 flex-wrap mb-2">
                    <span className="font-fraunces italic text-[var(--cream)]">@{m.external_handle}</span>
                    <span className="font-meta text-[9px] tracking-[0.15em] text-[var(--cream-faint)]">{plat}</span>
                    {m.slot_type && (
                      <span className="font-meta text-[9px] px-1.5 py-0.5 border border-[var(--magenta)]/60 text-[var(--magenta)] uppercase tracking-wider">
                        {m.slot_type}
                      </span>
                    )}
                    {m.handled && (
                      <span className="font-meta text-[9px] text-[var(--gold)]">✓ HÅNDTERT</span>
                    )}
                  </div>
                  {m.message && (
                    <p className="text-xs text-[var(--cream-soft)] whitespace-pre-line mb-2 line-clamp-3">{m.message}</p>
                  )}
                  <div className="flex flex-wrap gap-2 mt-2">
                    <button onClick={() => releaseExtDate(m)} type="button"
                      disabled={manualBusy === m.id}
                      className="btn-ghost text-[10px] inline-flex items-center gap-1 border-[var(--gold)]/40 text-[var(--gold)] hover:bg-[var(--gold)]/10"
                      data-testid={`ext-release-${m.id}`}>
                      Frigjør dato
                    </button>
                    <button onClick={() => toggleExtHandled(m)} type="button"
                      disabled={manualBusy === m.id}
                      className="btn-ghost text-[10px] inline-flex items-center gap-1"
                      data-testid={`ext-handled-${m.id}`}>
                      {m.handled ? "Angre håndtert" : "Marker håndtert"}
                    </button>
                    <Link to={`/skaper/samarbeid/henvendelse/${m.id}`} onClick={onClose}
                      className="btn-ghost text-[10px] inline-flex items-center gap-1"
                      data-testid={`ext-open-${m.id}`}>
                      <Send className="h-3 w-3" /> Åpne full detalj
                    </Link>
                  </div>

                  {/* Postet-status: begge parter (samme som manuell) */}
                  <div className="mt-3 pt-3 border-t border-white/10 space-y-2">
                    <label className="flex items-center gap-2 cursor-pointer text-xs text-[var(--cream)]">
                      <input
                        type="checkbox"
                        checked={!!m.posted_by_creator}
                        onChange={() => toggleExtPosted(m, "creator")}
                        disabled={manualBusy === m.id}
                        className="w-4 h-4 accent-[var(--magenta)] cursor-pointer"
                        data-testid={`ext-posted-me-${m.id}`}
                      />
                      <span className="flex-1">
                        {t("collab.manual_posted_by_me", "Postet fra min side")}
                        {m.posted_by_creator_at && (
                          <span className="ml-2 text-[10px] font-meta text-[var(--cream-faint)]">
                            ({new Date(m.posted_by_creator_at).toLocaleString("nb-NO")})
                          </span>
                        )}
                        {savedFlash[`${m.id}_creator`] && (
                          <span className="ml-2 text-[10px] font-meta text-[#46B85B]">
                            ✓ {t("collab.saved_at", "Lagret")} {savedFlash[`${m.id}_creator`]}
                          </span>
                        )}
                      </span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer text-xs text-[var(--cream)]">
                      <input
                        type="checkbox"
                        checked={!!m.posted_by_partner}
                        onChange={() => toggleExtPosted(m, "partner")}
                        disabled={manualBusy === m.id}
                        className="w-4 h-4 accent-[var(--magenta)] cursor-pointer"
                        data-testid={`ext-posted-partner-${m.id}`}
                      />
                      <span className="flex-1">
                        {t("collab.manual_posted_by_partner", "Postet fra motparten")}
                        {m.external_handle && (
                          <span className="text-[var(--cream-soft)]"> (@{m.external_handle})</span>
                        )}
                        {m.posted_by_partner_at && (
                          <span className="ml-2 text-[10px] font-meta text-[var(--cream-faint)]">
                            ({new Date(m.posted_by_partner_at).toLocaleString("nb-NO")})
                          </span>
                        )}
                        {savedFlash[`${m.id}_partner`] && (
                          <span className="ml-2 text-[10px] font-meta text-[#46B85B]">
                            ✓ {t("collab.saved_at", "Lagret")} {savedFlash[`${m.id}_partner`]}
                          </span>
                        )}
                      </span>
                    </label>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {requests.length === 0 ? (
          externalMessages.length === 0 && manualAgreements.length === 0 && !(slot && !slot.is_available) && (
            <p className="text-sm text-[var(--cream-soft)]">
              {t("collab.day_empty", "Ingen avtaler denne dagen. Gå til «Legg inn ledig» om du vil markere deg åpen.")}
            </p>
          )
        ) : (
          <ul className="space-y-3">
            {requests.map((r) => (
              <li key={r.id} className="border border-white/15 p-3" data-testid={`day-req-${r.id}`}>
                <div className="flex items-center gap-3 mb-2">
                  <span className="block w-2 h-2 rounded-full" style={{ backgroundColor: TYPE_COLOR[r.type] || "var(--cream-faint)" }} />
                  <div className="flex-1 min-w-0">
                    <div className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-soft)] uppercase truncate">
                      {r.type.toUpperCase()} · {STATUS_LABEL[r.status]?.toUpperCase() || r.status.toUpperCase()}
                    </div>
                    <div className="font-fraunces italic text-[var(--cream)] truncate">@{r.requester_id === user?.id ? r.target_username : r.requester_username}</div>
                  </div>
                  <button onClick={() => onOpenRequest(r.id)} className="text-xs font-meta text-[var(--magenta)] hover:underline shrink-0">
                    {t("collab.open", "Åpne")}
                  </button>
                </div>

                {/* TOSIDIG: Din del + Motpartens del */}
                {(r.status === "accepted" || r.status === "posted" || r.status === "confirmed") && (() => {
                  const isReq = r.requester_id === user?.id;
                  const mine = isReq ? "requester" : "target";
                  const other = isReq ? "target" : "requester";
                  const myPosted = r[`${mine}_posted_at`];
                  const otherPosted = r[`${other}_posted_at`];
                  const myProof = r[`${mine}_proof_url`];
                  const otherProof = r[`${other}_proof_url`];
                  const myConf = r[`${mine}_confirmed_at`];
                  const otherConf = r[`${other}_confirmed_at`];
                  return (
                    <div className="space-y-3 text-sm">
                      {/* DIN DEL */}
                      <div className="border-l-2 border-[var(--magenta)] pl-3">
                        <div className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] mb-1.5">{t("collab.your_part", "DIN DEL")}</div>
                        {!myPosted ? (
                          <label className="flex items-center gap-2 cursor-pointer mb-2">
                            <input type="checkbox" checked={false} disabled={busy === r.id} onChange={() => markPosted(r)} className="h-4 w-4 accent-[var(--magenta)]" data-testid={`mark-posted-${r.id}`} />
                            <span className="text-[var(--cream)]">{t("collab.mark_posted", "Marker som postet")}</span>
                          </label>
                        ) : (
                          <div className="text-xs text-[var(--gold)] mb-2 inline-flex items-center gap-1.5">
                            <span className="block w-1.5 h-1.5 rounded-full bg-[var(--gold)]" />
                            {t("collab.posted_label", "Postet")} ✓
                          </div>
                        )}
                        <div className="flex items-center gap-2">
                          {myProof ? (
                            <>
                              <a href={myProof} target="_blank" rel="noopener noreferrer" onClick={(e) => { e.preventDefault(); authView(myProof); }} className="text-xs font-meta text-[var(--gold)] hover:underline" data-testid={`proof-view-${r.id}`}>{t("collab.proof_view", "Se bevis")}</a>
                              <label className="text-xs font-meta text-[var(--cream-faint)] hover:text-[var(--magenta)] cursor-pointer">
                                {t("collab.proof_replace", "Bytt")}
                                <input type="file" accept="image/*" className="hidden" onChange={(e) => { if (e.target.files?.[0]) uploadProof(r, e.target.files[0]); e.target.value = ""; }} data-testid={`proof-replace-${r.id}`} />
                              </label>
                            </>
                          ) : (
                            <label className="inline-flex items-center gap-1.5 px-2 py-1 border border-white/15 text-xs font-meta text-[var(--cream-soft)] hover:border-[var(--magenta)]/40 hover:text-[var(--magenta)] cursor-pointer">
                              <Plus className="h-3 w-3" />
                              {t("collab.proof_upload", "Last opp bevis")}
                              <input type="file" accept="image/*" className="hidden" disabled={busy === r.id} onChange={(e) => { if (e.target.files?.[0]) uploadProof(r, e.target.files[0]); e.target.value = ""; }} data-testid={`proof-upload-${r.id}`} />
                            </label>
                          )}
                        </div>
                      </div>

                      {/* MOTPART */}
                      <div className="border-l-2 border-white/15 pl-3">
                        <div className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] mb-1.5">
                          {t("collab.other_part", "MOTPART")} · @{isReq ? r.target_username : r.requester_username}
                        </div>
                        {otherPosted ? (
                          <>
                            <div className="text-xs text-[var(--gold)] mb-2 inline-flex items-center gap-1.5">
                              <span className="block w-1.5 h-1.5 rounded-full bg-[var(--gold)]" />
                              {t("collab.posted_label", "Postet")} ✓
                            </div>
                            {otherProof && (
                              <a href={otherProof} target="_blank" rel="noopener noreferrer" onClick={(e) => { e.preventDefault(); authView(otherProof); }} className="text-xs font-meta text-[var(--gold)] hover:underline block mb-2" data-testid={`other-proof-view-${r.id}`}>{t("collab.proof_view", "Se bevis")}</a>
                            )}
                            {!myConf ? (
                              <button onClick={() => confirmRequest(r)} disabled={busy === r.id} className="px-3 py-1.5 text-xs font-meta border border-[var(--magenta)] text-[var(--magenta)] hover:bg-[var(--magenta)] hover:text-white transition-colors" data-testid={`confirm-${r.id}`}>
                                {t("collab.confirm_seen", "Bekreft mottatt")}
                              </button>
                            ) : (
                              <div className="text-xs text-[var(--gold)] inline-flex items-center gap-1.5">
                                <span className="block w-1.5 h-1.5 rounded-full bg-[var(--gold)]" />
                                {t("collab.confirmed_by_you", "Bekreftet av deg")} ✓
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="text-xs text-[var(--cream-faint)] italic">{t("collab.waiting_other", "Venter på motpart …")}</div>
                        )}
                        {otherConf && (
                          <div className="text-xs text-[var(--gold)] mt-1 inline-flex items-center gap-1.5">
                            <span className="block w-1.5 h-1.5 rounded-full bg-[var(--gold)]" />
                            {t("collab.confirmed_by_other", "Bekreftet av motpart")} ✓
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function TypeLegend({ color, label }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
      <span className="font-meta text-[10px] tracking-[0.15em]">{label}</span>
    </div>
  );
}


function RequestsInbox({ onOpen, reload }) {
  const { t } = useTranslation();
  const [incoming, setIncoming] = useState([]);
  const [outgoing, setOutgoing] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [i, o] = await Promise.all([
        api.get("/collab/requests?direction=incoming"),
        api.get("/collab/requests?direction=outgoing"),
      ]);
      setIncoming((i.data.results || []).filter((r) => r.status === "proposed"));
      setOutgoing((o.data.results || []).filter((r) => r.status === "proposed"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (cancelled) return;
      await load();
    })();
    return () => { cancelled = true; };
  }, []);

  const act = async (id, action) => {
    try {
      await api.post(`/collab/requests/${id}/${action}`, {});
      toast.success(t(`collab.action_${action}_ok`));
      load();
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("collab.save_err"));
    }
  };

  if (loading) return null;
  if (incoming.length === 0 && outgoing.length === 0) return null;

  return (
    <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
      {incoming.length > 0 && (
        <div className="panel-tight p-4">
          <h3 className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] mb-3">
            {t("collab.incoming_label")} ({incoming.length})
          </h3>
          <ul className="space-y-2">
            {incoming.map((r) => (
              <li key={r.id} className="text-sm" data-testid={`incoming-${r.id}`}>
                <button onClick={() => onOpen(r.id)} className="block text-left w-full hover:underline">
                  <strong className="text-[var(--cream)]">{r.requester_username}</strong>
                  <span className="text-[var(--cream-soft)]"> – {r.type.toUpperCase()} – {r.scheduled_date}</span>
                </button>
                <div className="flex gap-2 mt-1">
                  <button onClick={() => act(r.id, "accept")} className="text-xs font-meta text-[var(--gold)] hover:underline">
                    {t("collab.accept")}
                  </button>
                  <button onClick={() => act(r.id, "decline")} className="text-xs font-meta text-red-300 hover:underline">
                    {t("collab.decline")}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
      {outgoing.length > 0 && (
        <div className="panel-tight p-4">
          <h3 className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] mb-3">
            {t("collab.outgoing_label")} ({outgoing.length})
          </h3>
          <ul className="space-y-2">
            {outgoing.map((r) => (
              <li key={r.id} className="text-sm" data-testid={`outgoing-${r.id}`}>
                <button onClick={() => onOpen(r.id)} className="block text-left w-full hover:underline">
                  <strong className="text-[var(--cream)]">{r.target_username}</strong>
                  <span className="text-[var(--cream-soft)]"> – {r.type.toUpperCase()} – {r.scheduled_date}</span>
                </button>
                <button onClick={() => act(r.id, "cancel")} className="text-xs font-meta text-[var(--cream-faint)] hover:underline mt-1">
                  {t("collab.cancel")}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
